import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Edit, Trash2, X, Check, Star, GripVertical } from 'lucide-react';
import toast from 'react-hot-toast';
import { packagesService, Package, getErrorMessage } from '@/services';
import { clsx } from 'clsx';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const packageSchema = z.object({
  name: z.string().min(1, 'validation.required'),
  duration_days: z.coerce.number().min(0),
  is_trial: z.boolean(),
  is_default: z.boolean(),
  description: z.string().optional(),
  is_active: z.boolean(),
});

type PackageForm = z.infer<typeof packageSchema>;

interface SortableRowProps {
  pkg: Package;
  onEdit: (pkg: Package) => void;
  onDelete: (id: number) => void;
  onSetDefault: (id: number) => void;
  deleteConfirm: number | null;
  setDeleteConfirm: (id: number | null) => void;
  t: (key: string) => string;
}

function SortableRow({
  pkg,
  onEdit,
  onDelete,
  onSetDefault,
  deleteConfirm,
  setDeleteConfirm,
  t,
}: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: pkg.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style}>
      <td className="w-10">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing p-1 text-dark-400 hover:text-dark-200"
          {...attributes}
          {...listeners}
        >
          <GripVertical size={16} />
        </button>
      </td>
      <td className="font-medium text-dark-100">
        <div className="flex items-center gap-2">
          {pkg.name}
          {pkg.is_default === 1 && (
            <span className="badge-success text-xs">{t('packages.default')}</span>
          )}
          {pkg.is_trial === 1 && (
            <span className="badge-warning text-xs">{t('packages.trial')}</span>
          )}
        </div>
      </td>
      <td>
        {pkg.duration_days === 0
          ? t('packages.unlimited')
          : `${pkg.duration_days} ${t('packages.days')}`}
      </td>
      <td>
        <span className={pkg.is_active ? 'badge-success' : 'badge-error'}>
          {pkg.is_active ? t('common.active') : t('common.inactive')}
        </span>
      </td>
      <td>{pkg.user_count || 0}</td>
      <td>
        {deleteConfirm === pkg.id ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => onDelete(pkg.id)}
              className="p-1.5 rounded-lg bg-error-600 text-white hover:bg-error-500"
            >
              <Check size={16} />
            </button>
            <button
              onClick={() => setDeleteConfirm(null)}
              className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {pkg.is_default !== 1 && (
              <button
                onClick={() => onSetDefault(pkg.id)}
                className="p-1.5 rounded-lg hover:bg-warning-50 text-dark-400 hover:text-warning-600"
                title={t('packages.setDefault')}
              >
                <Star size={16} />
              </button>
            )}
            <button
              onClick={() => onEdit(pkg)}
              className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400 hover:text-dark-100"
            >
              <Edit size={16} />
            </button>
            {pkg.is_default !== 1 && (pkg.user_count || 0) === 0 && (
              <button
                onClick={() => setDeleteConfirm(pkg.id)}
                className="p-1.5 rounded-lg hover:bg-error-50 text-dark-400 hover:text-error-600"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

export default function AdminPackages() {
  const { t } = useTranslation();
  const [packages, setPackages] = useState<Package[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState<Package | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PackageForm>({
    resolver: zodResolver(packageSchema),
    defaultValues: {
      is_trial: false,
      is_default: false,
      is_active: true,
      duration_days: 30,
    },
  });

  const fetchPackages = async () => {
    try {
      const data = await packagesService.list();
      setPackages(data);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  const openCreateModal = () => {
    setEditingPackage(null);
    reset({
      name: '',
      duration_days: 30,
      is_trial: false,
      is_default: false,
      description: '',
      is_active: true,
    });
    setShowModal(true);
  };

  const openEditModal = (pkg: Package) => {
    setEditingPackage(pkg);
    reset({
      name: pkg.name,
      duration_days: pkg.duration_days,
      is_trial: pkg.is_trial === 1,
      is_default: pkg.is_default === 1,
      description: pkg.description || '',
      is_active: pkg.is_active === 1,
    });
    setShowModal(true);
  };

  const onSubmit = async (data: PackageForm) => {
    try {
      if (editingPackage) {
        await packagesService.update(editingPackage.id, data);
        toast.success(t('packages.updated'));
      } else {
        await packagesService.create(data);
        toast.success(t('packages.created'));
      }
      setShowModal(false);
      fetchPackages();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await packagesService.delete(id);
      toast.success(t('packages.deleted'));
      setDeleteConfirm(null);
      fetchPackages();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('packages.deleteError'));
    }
  };

  const handleSetDefault = async (id: number) => {
    try {
      await packagesService.setDefault(id);
      toast.success(t('packages.defaultSet'));
      fetchPackages();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = packages.findIndex((p) => p.id === active.id);
    const newIndex = packages.findIndex((p) => p.id === over.id);

    const newPackages = arrayMove(packages, oldIndex, newIndex);
    setPackages(newPackages);

    try {
      await packagesService.reorder(
        newPackages.map((p, index) => ({ id: p.id, sort_order: index }))
      );
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
      fetchPackages();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">{t('packages.title')}</h1>
          <p className="text-dark-400 mt-1">{t('packages.subtitle')}</p>
        </div>
        <button onClick={openCreateModal} className="btn-primary">
          <Plus size={18} />
          {t('packages.add')}
        </button>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="card">
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-dark-700 rounded" />
            ))}
          </div>
        </div>
      ) : packages.length === 0 ? (
        <div className="card">
          <p className="text-dark-400">{t('packages.noData')}</p>
        </div>
      ) : (
        <div className="table-container">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table className="table">
              <thead>
                <tr>
                  <th className="w-10"></th>
                  <th>{t('packages.name')}</th>
                  <th>{t('packages.duration')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('admin.users')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <SortableContext
                items={packages.map((p) => p.id)}
                strategy={verticalListSortingStrategy}
              >
                <tbody>
                  {packages.map((pkg) => (
                    <SortableRow
                      key={pkg.id}
                      pkg={pkg}
                      onEdit={openEditModal}
                      onDelete={handleDelete}
                      onSetDefault={handleSetDefault}
                      deleteConfirm={deleteConfirm}
                      setDeleteConfirm={setDeleteConfirm}
                      t={t}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </table>
          </DndContext>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowModal(false)}
        >
          <div
            className="card w-full max-w-lg m-4 animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-dark-100">
                {editingPackage ? t('packages.edit') : t('packages.add')}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="label">{t('packages.name')}</label>
                <input
                  type="text"
                  className={clsx('input', errors.name && 'input-error')}
                  placeholder={t('packages.namePlaceholder')}
                  {...register('name')}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-error-500">
                    {t(errors.name.message || '')}
                  </p>
                )}
              </div>

              <div>
                <label className="label">{t('packages.duration')}</label>
                <input
                  type="number"
                  min="0"
                  className={clsx('input', errors.duration_days && 'input-error')}
                  {...register('duration_days')}
                />
                <p className="mt-1 text-xs text-dark-500">{t('packages.durationHelp')}</p>
              </div>

              <div>
                <label className="label">{t('common.description')}</label>
                <textarea
                  className="input"
                  rows={2}
                  {...register('description')}
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_trial"
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500"
                    {...register('is_trial')}
                  />
                  <label htmlFor="is_trial" className="text-sm text-dark-200">
                    {t('packages.isTrial')}
                  </label>
                </div>
                <p className="text-xs text-dark-500 ml-6">{t('packages.isTrialHelp')}</p>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500"
                    {...register('is_active')}
                  />
                  <label htmlFor="is_active" className="text-sm text-dark-200">
                    {t('packages.isActive')}
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary"
                >
                  {isSubmitting ? t('common.loading') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
